import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useImmerReducer } from 'use-immer';
import { Link } from 'react-router-dom';
import format from 'date-fns/format';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';

import { createAxiosInstance } from 'src/utils/createAxiosInstance';
import { keepFileAlphanumeric } from 'src/utils/utility';
import { InvoiceFileProcessingStatusDto, InvoiceUploadSignedUrlDto } from 'src/generated/services/TFinancialApi';
import { useGetFileUrl } from 'src/use-cases/internalDashboard/useFileResolution';
import { useNotifyFileUploaded } from 'src/use-cases/invoice/useInvoices';
import { useUploadUI } from './useUploadUI';
import { processingStatuses } from './configs';
import { renderInvoiceStatusMessages, renderInvoiceStatusComponent } from './uploadStatusUtils';
import { CustomTooltip } from './CustomTooltip';
import { IGroupingItemData } from './types';
import { FileList } from '../shared/FileList';
import { DocumentIconWithModalities } from '../shared/DocumentIconWithModalities';

const axios = createAxiosInstance({}, []);

// Global upload tracking - persists across component re-renders/remounts
// Using presignedURL as key since it's unique per upload
const uploadedFiles = new Set<string>();

export interface IUploadStatusItemProps {
  data?: InvoiceUploadSignedUrlDto; // Single file (legacy)
  dataList?: IGroupingItemData[]; // Multiple files grouped by invoice
  onFinish?(data: InvoiceUploadSignedUrlDto): void; // Made optional - upload completion now tracked by useUploadData
  invoiceDetail?: InvoiceFileProcessingStatusDto | null;
  isDisableUpload?: boolean;
}

interface FileProps {
  name: string;
  date: string;
}

interface UploadState {
  uploadPercentage: number;
  // fileUpload will be non-null while uploading
  fileUpload: FileProps;
  uploadError: string;
}

type UploadAction =
  | {
      type: 'uploading';
      payload: number;
    }
  | {
      type: 'showUploadingFile';
      payload: FileProps;
    }
  | {
      type: 'error';
      payload: string;
    }
  | {
      type: 'uploadComplete';
    };

const uploadReducer = (state: UploadState, action: UploadAction): UploadState => {
  switch (action.type) {
    case 'uploading':
      state.uploadPercentage = action.payload;
      return state;
    case 'showUploadingFile':
      state.uploadPercentage = 0;
      state.fileUpload = action.payload;
      state.uploadError = '';
      return state;
    case 'uploadComplete':
      state.fileUpload = null;
      state.uploadPercentage = 100; // Ensure percentage is set to 100 when upload completes
      return state;
    case 'error':
      state.fileUpload = null;
      state.uploadError = action.payload;
      return state;

    default:
      return state;
  }
};

const initialState: UploadState = {
  fileUpload: null,
  uploadError: '',
  uploadPercentage: 0,
};

export const UploadStatusItem: FC<IUploadStatusItemProps> = ({ data, dataList, onFinish, invoiceDetail, isDisableUpload }) => {
  // Support both single file (data + invoiceDetail) and multiple files (dataList)
  const files = dataList || (data ? [{ ...data, invoiceDetail }] : []);
  const primaryFile = files[0]; // Use first file for main data
  const hasMultipleFiles = files.length > 1;

  const { invoiceFiles, errorProcessingStatuses } = useUploadUI();
  const { mutate: notifyFileUploaded } = useNotifyFileUploaded();
  const [state, dispatch] = useImmerReducer(uploadReducer, initialState);
  const isMountedRef = useRef<boolean>(true);

  // Memoize these values to prevent effect re-runs when parent re-renders
  const preSignedURLs = useMemo(() => primaryFile?.preSignedURLs, [primaryFile?.preSignedURLs]);
  const headers = useMemo(() => primaryFile?.headers, [primaryFile?.headers]);
  const fileName = useMemo(() => primaryFile?.fileName, [primaryFile?.fileName]);

  const primaryInvoiceDetail = primaryFile?.invoiceDetail || null;
  const { refetch: getFileUrl } = useGetFileUrl(primaryInvoiceDetail?.invoiceFileProcessingId);

  // Tooltip state with 0.5 second delay
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  /**
   * Memoized value to determine if the current status is a processing status.
   */
  const isProcessing = useMemo(
    () =>
      processingStatuses.includes(primaryInvoiceDetail?.status || '') ||
      (primaryInvoiceDetail?.processingShipmentCount && primaryInvoiceDetail.processingShipmentCount > 0),
    [primaryInvoiceDetail]
  );

  /**
   * Check if status is one that requires special "Other" section styling
   * (Under Review or Resolution statuses)
   */
  const isOtherSectionStatus = useMemo(() => {
    const otherStatuses = ['RESOLVED_AS_NOT_AN_INVOICE', 'RESOLVED_AS_MISSING_REQUIRED_FIELD', 'RESOLVED_AS_INCONSISTENT_SHIPMENT_DATA'];
    const isResolutionStatus = otherStatuses.includes(primaryInvoiceDetail?.status || '');
    const isErrorStatus = errorProcessingStatuses.includes(primaryInvoiceDetail?.status || '');
    return isResolutionStatus || isErrorStatus;
  }, [primaryInvoiceDetail, errorProcessingStatuses]);

  // Determine action type
  const isInvoiceLink = primaryInvoiceDetail?.invoiceId && primaryInvoiceDetail?.shipmentCount !== primaryInvoiceDetail?.processingShipmentCount;
  const isDisabled = primaryInvoiceDetail?.status === 'BEING_UPLOADED';

  // Handle click action
  const handleClick = () => {
    if (isDisabled) return;
    if (!isInvoiceLink) {
      onSeeFile();
    }
  };

  // Generate invoice link path
  const getInvoiceLinkPath = () => `/action-details/${primaryInvoiceDetail?.invoiceId}?hideBack=1&skipBilledEntityReload=true`;

  useEffect(() => {
    // Early return if already uploaded (check global Set) or conditions not met
    if (!preSignedURLs || preSignedURLs === '' || isDisableUpload || uploadedFiles.has(preSignedURLs)) {
      return;
    }

    const file = invoiceFiles.find((file: File) => keepFileAlphanumeric(file?.name) === keepFileAlphanumeric(fileName));
    if (!file) {
      return;
    }

    // Mark as uploaded IMMEDIATELY in global Set to prevent duplicate uploads across component instances
    uploadedFiles.add(preSignedURLs);

    axios
      .put(preSignedURLs, file, {
        onUploadProgress: (progressEvent) => {
          if (isMountedRef.current) {
            const percentage = Math.floor(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            dispatch({ type: 'uploading', payload: percentage });
          }
        },
        method: 'PUT',
        headers: {
          'Content-Type': 'application/pdf',
          ...headers,
        },
      })
      .then(() => {
        if (isMountedRef.current) {
          dispatch({ type: 'uploadComplete' });
          if (onFinish) {
            onFinish(data);
          }
        }
        if (primaryFile?.dataPipelineProcessingId) {
          notifyFileUploaded(primaryFile.dataPipelineProcessingId);
        }
      })
      .catch((error) => {
        // Remove from tracking on error so it can be retried
        uploadedFiles.delete(preSignedURLs);
        if (isMountedRef.current) {
          dispatch({ type: 'error', payload: error?.message || 'Error' });
          if (onFinish) {
            onFinish(data);
          }
        }
      });
  }, [data, dispatch, fileName, headers, invoiceFiles, onFinish, preSignedURLs, isDisableUpload]);

  // Cleanup: set mounted flag to false on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onSeeFile = async () => {
    const result = await getFileUrl();
    if (result?.data) {
      window.open(result?.data, '_blank');
    }
  };

  // Handle mouse move to track cursor position
  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse enter with 0.5 second delay
  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  };

  // Handle mouse leave - clear timeout and hide tooltip
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  // Render card content
  const cardContent = (
    <div className='relative flex rounded-10 bg-neutral-10 flex-col border border-neutral-20 pt-24 hover:border-primary transition-colors'>
      {/* top info */}
      <div className='flex items-center gap-16 px-16'>
        <DocumentIconWithModalities
          modalities={primaryInvoiceDetail?.modalities}
          isProcessing={isProcessing}
          invoiceFileProcessingId={primaryInvoiceDetail?.invoiceFileProcessingId}
        />
        <div className='flex-1 flex flex-col gap-2'>
          {/* Wrapper with conditional className to hide when invoice number is invalid */}
          <div
            className={
              !primaryInvoiceDetail?.invoiceNumber || primaryInvoiceDetail?.invoiceNumber === '0' || primaryInvoiceDetail?.invoiceNumber === ''
                ? 'hidden'
                : 'flex'
            }
          >
            {primaryInvoiceDetail?.modalities &&
            primaryInvoiceDetail?.invoiceNumber &&
            primaryInvoiceDetail?.invoiceNumber !== '0' &&
            primaryInvoiceDetail?.invoiceNumber !== '' ? (
              <p className='text-14 xl:text-16 font-bold text-primary flex items-center gap-x-8 select-text cursor-text'>
                Inv No: {primaryInvoiceDetail?.invoiceNumber}
              </p>
            ) : (
              <>{isProcessing && <div className='h-12 w-[130px] bg-[#DFDFDF] rounded-[3px]' />}</>
            )}
          </div>
          {/* Show file list when multiple files, single filename when one file */}
          <FileList
            files={files}
            hasMultipleFiles={hasMultipleFiles}
            isOtherSectionStatus={isOtherSectionStatus || isProcessing}
            renderStatusChip={(file, size) => renderInvoiceStatusComponent(file, errorProcessingStatuses, { size: size || 'small' })}
          />
        </div>

        <div className='ml-auto flex items-center gap-14 justify-end'>
          {/* Show upload progress ONLY when actively uploading AND no status info available */}
          {state.uploadPercentage < 100 &&
            !primaryInvoiceDetail?.status &&
            !primaryInvoiceDetail?.invoiceStatus &&
            !primaryInvoiceDetail?.fileResolutionStatus && (
              <div className='flex items-center justify-center gap-6 px-11 py-2 rounded-20 border border-grey-15 bg-white'>
                {/* Circular progress */}
                <div className='relative w-12 h-12'>
                  <svg className='w-12 h-12 transform -rotate-90' viewBox='0 0 12 12'>
                    {/* Background circle */}
                    <circle cx='6' cy='6' r='5' stroke='#E5E5E5' strokeWidth='2' fill='none' />
                    {/* Progress circle */}
                    <circle
                      cx='6'
                      cy='6'
                      r='5'
                      stroke={state.uploadError !== '' ? '#F87171' : '#00992B'}
                      strokeWidth='2'
                      fill='none'
                      strokeLinecap='round'
                      strokeDasharray={`${2 * Math.PI * 5}`}
                      strokeDashoffset={`${2 * Math.PI * 5 * (1 - state.uploadPercentage / 100)}`}
                      className='transition-all duration-300 ease-in-out'
                    />
                  </svg>
                </div>
                <span className='text-12 font-medium' style={{ color: '#00992B' }}>
                  {state.uploadPercentage}%
                </span>
                <span className='text-12 font-medium' style={{ color: '#00992B' }}>
                  Uploading
                </span>
              </div>
            )}

          {/* Show invoice status (APPROVED, FLAGGED, etc.) OR fileResolutionStatus (for Other/Under Review section) OR file status */}
          {primaryInvoiceDetail?.invoiceStatus ? (
            <>
              {renderInvoiceStatusMessages(primaryInvoiceDetail, errorProcessingStatuses, { showDot: false })}
              {/* Pass only invoiceStatus to avoid priority conflicts with file status */}
              {renderInvoiceStatusComponent({ invoiceStatus: primaryInvoiceDetail.invoiceStatus }, [], { size: 'medium' })}
            </>
          ) : primaryInvoiceDetail?.fileResolutionStatus ? (
            // For files with fileResolutionStatus (Other/Under Review section):
            // - UNDER_REVIEW: Show "Under Review" with tooltip (pass to errorProcessingStatuses)
            // - Others (INCONSISTENT_SHIPMENT_DATA, etc.): Show their own status chip
            renderInvoiceStatusComponent(
              { status: primaryInvoiceDetail.fileResolutionStatus },
              primaryInvoiceDetail.fileResolutionStatus === 'UNDER_REVIEW' ? ['UNDER_REVIEW'] : [],
              { size: 'medium' }
            )
          ) : (
            primaryInvoiceDetail?.status !== 'DUPLICATE' && renderInvoiceStatusComponent(primaryInvoiceDetail, errorProcessingStatuses, { size: 'medium' })
          )}
        </div>
      </div>
      {/* counter */}
      <div className='flex items-center px-16 justify-between pt-8 pb-10 mt-16 rounded-8 border border-neutral-20'>
        {!primaryInvoiceDetail?.updatedOn ? (
          <div className='mt-4 h-12 w-[210px] bg-[#DFDFDF] rounded-[3px]' />
        ) : (
          <>
            <p className='mt-2 text-12 xl:text-14 text-neutral-60 flex items-center gap-4'>
              <AccessTimeIcon sx={{ fontSize: '14px', color: '#9F9F9F' }} />
              Updated on {format(new Date(primaryInvoiceDetail?.updatedOn), 'dd-MMM-yyyy')} at {format(new Date(primaryInvoiceDetail?.updatedOn), 'hh.mm a')}
            </p>
            {/* Shipment count information - only show if there's meaningful data */}
            {primaryInvoiceDetail?.shipmentCount &&
            primaryInvoiceDetail.shipmentCount > 0 &&
            (primaryInvoiceDetail?.processingShipmentCount > 0 ||
              primaryInvoiceDetail?.flaggedShipmentCount > 0 ||
              primaryInvoiceDetail?.resolvedShipmentCount > 0 ||
              primaryInvoiceDetail?.underReviewShipmentCount > 0) ? (
              <div className='mt-2 flex items-center gap-x-8 text-neutral-60 text-10 xl:text-12'>
                {primaryInvoiceDetail?.processingShipmentCount !== undefined && primaryInvoiceDetail.processingShipmentCount > 0 && (
                  <>
                    <span>
                      Processing {primaryInvoiceDetail.processingShipmentCount}/{primaryInvoiceDetail.shipmentCount}
                    </span>
                    {/* Show separator if there are other counts to display */}
                    {((primaryInvoiceDetail?.flaggedShipmentCount !== undefined && primaryInvoiceDetail.flaggedShipmentCount > 0) ||
                      (primaryInvoiceDetail?.resolvedShipmentCount !== undefined && primaryInvoiceDetail.resolvedShipmentCount > 0) ||
                      (primaryInvoiceDetail?.underReviewShipmentCount !== undefined && primaryInvoiceDetail.underReviewShipmentCount > 0)) && (
                      <span style={{ color: '#DFDFDF' }}>|</span>
                    )}
                  </>
                )}
                {primaryInvoiceDetail?.flaggedShipmentCount !== undefined && primaryInvoiceDetail.flaggedShipmentCount > 0 && (
                  <>
                    <span>
                      With Audit Exceptions {primaryInvoiceDetail.flaggedShipmentCount}/{primaryInvoiceDetail.shipmentCount}
                    </span>
                    {/* Show separator if there are other counts to display after this */}
                    {((primaryInvoiceDetail?.resolvedShipmentCount !== undefined && primaryInvoiceDetail.resolvedShipmentCount > 0) ||
                      (primaryInvoiceDetail?.underReviewShipmentCount !== undefined && primaryInvoiceDetail.underReviewShipmentCount > 0)) && (
                      <span style={{ color: '#DFDFDF' }}>|</span>
                    )}
                  </>
                )}
                {primaryInvoiceDetail?.resolvedShipmentCount !== undefined && primaryInvoiceDetail.resolvedShipmentCount > 0 && (
                  <>
                    <span>
                      Resolved {primaryInvoiceDetail.resolvedShipmentCount}/{primaryInvoiceDetail.shipmentCount}
                    </span>
                    {/* Show separator if there are other counts to display after this */}
                    {primaryInvoiceDetail?.underReviewShipmentCount !== undefined && primaryInvoiceDetail.underReviewShipmentCount > 0 && (
                      <span style={{ color: '#DFDFDF' }}>|</span>
                    )}
                  </>
                )}

                {primaryInvoiceDetail?.underReviewShipmentCount !== undefined && primaryInvoiceDetail.underReviewShipmentCount > 0 && (
                  <span>
                    Under Review {primaryInvoiceDetail.underReviewShipmentCount}/{primaryInvoiceDetail.shipmentCount}
                  </span>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Tooltip rendered inside card */}
      <CustomTooltip show={showTooltip} invoiceDetail={primaryInvoiceDetail} mouseX={mousePosition.x} mouseY={mousePosition.y} />
    </div>
  );

  // Return with conditional wrapper
  if (isInvoiceLink) {
    return (
      <Link
        to={getInvoiceLinkPath()}
        target='_blank'
        className='block w-full'
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ opacity: isDisabled ? 0.4 : 1 }}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <button
      type='button'
      className='block w-full text-left'
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      disabled={isDisabled}
      style={{ cursor: isDisabled ? 'default' : 'pointer', opacity: isDisabled ? 0.4 : 1 }}
    >
      {cardContent}
    </button>
  );
};
